// Shopify Cart Restriction for POD Products - Aggressive Version
// This version completely hijacks form submissions

(function() {
  'use strict';

  // Configuration
  const POD_TAGS = ['print-on-demand']; // Add new tags, if multiple tags are used add it like this: ['pod', 'print-on-demand']
  const ERROR_MESSAGE_POD_IN_CART = 'Your cart already contains a Print-on-Demand product. Please complete this order separately or clear your cart before adding other products.';
  const ERROR_MESSAGE_REGULAR_IN_CART = 'Your cart already contains regular products. Print-on-Demand products must be ordered separately. Please clear your cart or complete your current order.';
  
  const DEBUG = false; // Set to true for debugging
  
  function debugLog(...args) {
    if (DEBUG) {
      console.log('[POD Cart Restriction Aggressive]', ...args);
    }
  }

  // Helper function to check if product has any POD tag
  function hasPodTag(productOrTags) {
    let tags = productOrTags;
    if (!tags) return false;
    if (typeof tags === 'object' && tags.tags) tags = tags.tags;
    if (typeof tags === 'string') {
      return POD_TAGS.some(tag => tags.toLowerCase().includes(tag.toLowerCase()));
    } else if (Array.isArray(tags)) {
      return tags.some(tag => POD_TAGS.some(podTag => tag.toLowerCase() === podTag.toLowerCase()));
    }
    return false;
  }

  // Function to check if cart has POD products
  async function cartHasPodProducts() {
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();
      
      if (!cart || !cart.items) return false;
      
      for (const item of cart.items) {
        const titleLower = item.title ? item.title.toLowerCase() : '';
        if (titleLower.includes('pod ') || titleLower.startsWith('pod')) {
          return true;
        }
        
        if (item.handle && item.handle.includes('pod-')) {
          return true;
        }
      }
      
      return false;
    } catch (error) {
      console.error('Error checking cart:', error);
      return false;
    }
  }

  // Function to show error message
  function showErrorMessage(message) {
    const existingError = document.querySelector('.pod-cart-error');
    if (existingError) {
      existingError.remove();
    }

    const errorDiv = document.createElement('div');
    errorDiv.className = 'pod-cart-error';
    errorDiv.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      background-color: #ff4444;
      color: white;
      padding: 15px 20px;
      border-radius: 5px;
      box-shadow: 0 2px 10px rgba(0,0,0,0.2);
      z-index: 9999;
      max-width: 400px;
      font-size: 14px;
      line-height: 1.5;
    `;
    errorDiv.textContent = message;

    document.body.appendChild(errorDiv);

    setTimeout(() => {
      errorDiv.remove();
    }, 5000);
  }

  // Minimal form interception - only add validation
  function interceptForms() {
    // Use event delegation instead of modifying forms
    document.addEventListener('submit', async function(e) {
      const form = e.target;
      
      // Check if this is an add to cart form
      if (!form.action || !form.action.includes('/cart/add')) {
        return; // Not our form, ignore
      }
      
      debugLog('Intercepting form submission');
      
      // Get form data
      const formData = new FormData(form);
      const variantId = formData.get('id');
      
      // Check product tags
      let isCurrentProductPod = false;
      const productTags = document.querySelector('[data-product-tags]');
      if (productTags) {
        const tags = productTags.getAttribute('data-product-tags');
        isCurrentProductPod = hasPodTag(tags);
      }
      
      // Check cart status
      const cartResponse = await fetch('/cart.js');
      const cart = await cartResponse.json();
      const cartIsEmpty = !cart.items || cart.items.length === 0;
      
      debugLog('Cart status:', { cartIsEmpty, itemCount: cart.items ? cart.items.length : 0 });
      
      // Only check restrictions if cart is not empty
      if (!cartIsEmpty) {
        const hasPodInCart = await cartHasPodProducts();
        
        debugLog('Check results:', { isCurrentProductPod, hasPodInCart });
        
        if (hasPodInCart && !isCurrentProductPod) {
          e.preventDefault();
          e.stopPropagation();
          e.stopImmediatePropagation();
          showErrorMessage(ERROR_MESSAGE_POD_IN_CART);
          return false;
        } else if (!hasPodInCart && isCurrentProductPod) {
          e.preventDefault();
          e.stopPropagation();
          e.stopImmediatePropagation();
          showErrorMessage(ERROR_MESSAGE_REGULAR_IN_CART);
          return false;
        }
      }
      
      // If we get here, all checks passed - let form submit naturally
      debugLog('All checks passed, allowing submission');
      // Don't call preventDefault - let it submit normally
    }, true); // Use capture phase to run before other handlers
  }

  // Intercept Fetch API
  function interceptFetch() {
    const originalFetch = window.fetch;
    
    window.fetch = async function(url, options = {}) {
      // Check if this is a cart/add request
      if (url && (url.includes('/cart/add') || url.includes('/cart/add.js'))) {
        debugLog('Intercepted fetch to cart/add');
        
        // Parse data
        let variantId = null;
        if (options.body) {
          if (options.body instanceof FormData) {
            variantId = options.body.get('id');
          } else if (typeof options.body === 'string') {
            try {
              const parsed = JSON.parse(options.body);
              variantId = parsed.id || parsed.variant_id;
            } catch (e) {
              const params = new URLSearchParams(options.body);
              variantId = params.get('id');
            }
          }
        }
        
        // Check restrictions
        let isCurrentProductPod = false;
        const productTags = document.querySelector('[data-product-tags]');
        if (productTags) {
          const tags = productTags.getAttribute('data-product-tags');
          isCurrentProductPod = hasPodTag(tags);
        }
        
        // Check cart first
        const cartResponse = await originalFetch('/cart.js');
        const cart = await cartResponse.json();
        const cartIsEmpty = !cart.items || cart.items.length === 0;
        
        // Only apply restrictions if cart is not empty
        if (!cartIsEmpty) {
          const hasPodInCart = await cartHasPodProducts();
          
          if (hasPodInCart && !isCurrentProductPod) {
            showErrorMessage(ERROR_MESSAGE_POD_IN_CART);
            // Return fake error response
            return new Response(JSON.stringify({ error: ERROR_MESSAGE_POD_IN_CART }), {
              status: 422,
              headers: { 'Content-Type': 'application/json' }
            });
          } else if (!hasPodInCart && isCurrentProductPod) {
            showErrorMessage(ERROR_MESSAGE_REGULAR_IN_CART);
            // Return fake error response
            return new Response(JSON.stringify({ error: ERROR_MESSAGE_REGULAR_IN_CART }), {
              status: 422,
              headers: { 'Content-Type': 'application/json' }
            });
          }
        }
      }
      
      // If not cart/add or checks passed, proceed normally
      return originalFetch.apply(this, arguments);
    };
  }

  // Intercept XMLHttpRequest
  function interceptXHR() {
    const originalXHR = window.XMLHttpRequest;
    
    window.XMLHttpRequest = function() {
      const xhr = new originalXHR();
      const originalOpen = xhr.open;
      const originalSend = xhr.send;
      
      xhr.open = function(method, url, ...args) {
        this._url = url;
        this._method = method;
        return originalOpen.apply(this, [method, url, ...args]);
      };
      
      xhr.send = async function(data) {
        if (this._url && this._url.includes('/cart/add')) {
          debugLog('Intercepted XHR to cart/add');
          
          // Parse data
          let variantId = null;
          if (data) {
            if (data instanceof FormData) {
              variantId = data.get('id');
            } else if (typeof data === 'string') {
              try {
                const parsed = JSON.parse(data);
                variantId = parsed.id || parsed.variant_id;
              } catch (e) {
                const params = new URLSearchParams(data);
                variantId = params.get('id');
              }
            }
          }
          
          // Check restrictions
          let isCurrentProductPod = false;
          const productTags = document.querySelector('[data-product-tags]');
          if (productTags) {
            const tags = productTags.getAttribute('data-product-tags');
            isCurrentProductPod = hasPodTag(tags);
          }
          
          // Check cart first
          const cartResponse = await fetch('/cart.js');
          const cart = await cartResponse.json();
          const cartIsEmpty = !cart.items || cart.items.length === 0;
          
          // Only apply restrictions if cart is not empty
          if (!cartIsEmpty) {
            const hasPodInCart = await cartHasPodProducts();
            
            if (hasPodInCart && !isCurrentProductPod) {
              showErrorMessage(ERROR_MESSAGE_POD_IN_CART);
              // Fake error response
              this.status = 422;
              this.readyState = 4;
              this.responseText = JSON.stringify({ error: ERROR_MESSAGE_POD_IN_CART });
              this.onreadystatechange && this.onreadystatechange();
              return;
            } else if (!hasPodInCart && isCurrentProductPod) {
              showErrorMessage(ERROR_MESSAGE_REGULAR_IN_CART);
              // Fake error response
              this.status = 422;
              this.readyState = 4;
              this.responseText = JSON.stringify({ error: ERROR_MESSAGE_REGULAR_IN_CART });
              this.onreadystatechange && this.onreadystatechange();
              return;
            }
          }
        }
        
        // If not cart/add or checks passed, proceed normally
        return originalSend.apply(this, arguments);
      };
      
      return xhr;
    };
  }

  // Initialize
  function init() {
    debugLog('Initializing aggressive version');
    
    // Intercept AJAX requests and form submissions
    interceptFetch();
    interceptXHR();
    interceptForms();
  }

  // Start
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();